import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/i18n/locales";
import {
  mergeContent,
  normalizeContent,
  type PublicReference,
  type ReferenceContent,
  type ReferenceRow,
} from "@/data/references";

export type PublicReview = {
  id: string;
  quote: string;
  author: string;
  role: string;
  rating: number;
  avatar_url: string;
};

export type PublicFaq = {
  id: string;
  question: string;
  answer: string;
};

export function useReviews() {
  return useQuery({
    queryKey: ["reviews"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("id, quote, author, role, rating, avatar_url")
        .eq("published", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as PublicReview[];
    },
  });
}

export function useFaqs(lang: Lang = "en") {
  return useQuery({
    queryKey: ["faqs", lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("faqs")
        .select("id, question, answer")
        .eq("published", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      const base = (data ?? []) as PublicFaq[];

      // English is the base content — no translation merge needed.
      if (lang === "en" || base.length === 0) return base;

      const { data: trData } = await supabase
        .from("faq_translations")
        .select("faq_id, question, answer")
        .eq("locale", lang)
        .in(
          "faq_id",
          base.map((f) => f.id)
        );
      const map = new Map(
        ((trData ?? []) as { faq_id: string; question: string; answer: string }[]).map(
          (t) => [t.faq_id, t]
        )
      );
      return base.map((f) => {
        const tr = map.get(f.id);
        return tr && tr.question.trim() && tr.answer.trim()
          ? { id: f.id, question: tr.question, answer: tr.answer }
          : f;
      });
    },
  });
}


const REFERENCE_COLUMNS =
  "id, slug, sort_order, featured, has_case_study, icon, logo_url, logo_invert, tags, en";

function toRow(r: Record<string, unknown>): ReferenceRow {
  return {
    id: r.id as string,
    slug: r.slug as string,
    sort_order: (r.sort_order as number) ?? 0,
    featured: Boolean(r.featured),
    published: true,
    has_case_study: Boolean(r.has_case_study),
    icon: (r.icon as string) ?? "Boxes",
    logo_url: (r.logo_url as string) ?? "",
    logo_invert: r.logo_invert !== false,
    tags: Array.isArray(r.tags) ? (r.tags as string[]) : [],
    en: normalizeContent(r.en),
  };
}

/** All published references, merged for the given language, ordered for the bento. */
export function useReferences(lang: Lang = "en") {
  return useQuery({
    queryKey: ["references", lang],
    queryFn: async (): Promise<PublicReference[]> => {
      const { data, error } = await supabase
        .from("references")
        .select(REFERENCE_COLUMNS)
        .eq("published", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      const rows = ((data ?? []) as Record<string, unknown>[]).map(toRow);
      if (rows.length === 0) return [];

      let trMap = new Map<string, Partial<ReferenceContent>>();
      if (lang !== "en") {
        const { data: trData } = await supabase
          .from("reference_translations")
          .select("reference_id, data")
          .eq("locale", lang)
          .in("reference_id", rows.map((r) => r.id));
        trMap = new Map(
          ((trData ?? []) as { reference_id: string; data: unknown }[]).map((t) => [
            t.reference_id,
            (t.data ?? {}) as Partial<ReferenceContent>,
          ]),
        );
      }

      return rows.map((r) => ({
        id: r.id,
        slug: r.slug,
        sort_order: r.sort_order,
        featured: r.featured,
        has_case_study: r.has_case_study,
        icon: r.icon,
        logo_url: r.logo_url,
        logo_invert: r.logo_invert,
        tags: r.tags,
        content: mergeContent(r.en, trMap.get(r.id)),
      }));
    },
  });
}

/** A single published reference by slug, merged for the given language. */
export function useReference(slug: string | undefined, lang: Lang = "en") {
  return useQuery({
    queryKey: ["reference", slug, lang],
    enabled: !!slug,
    queryFn: async (): Promise<PublicReference | null> => {
      const { data, error } = await supabase
        .from("references")
        .select(REFERENCE_COLUMNS)
        .eq("slug", slug!)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = toRow(data as Record<string, unknown>);

      let tr: Partial<ReferenceContent> | undefined;
      if (lang !== "en") {
        const { data: trData } = await supabase
          .from("reference_translations")
          .select("data")
          .eq("reference_id", row.id)
          .eq("locale", lang)
          .maybeSingle();
        tr = (trData?.data ?? undefined) as Partial<ReferenceContent> | undefined;
      }

      return {
        id: row.id,
        slug: row.slug,
        sort_order: row.sort_order,
        featured: row.featured,
        has_case_study: row.has_case_study,
        icon: row.icon,
        logo_url: row.logo_url,
        logo_invert: row.logo_invert,
        tags: row.tags,
        content: mergeContent(row.en, tr),
      };
    },
  });
}





export type SeoDefaults = { title: string; description: string; ogImage: string };
export type SeoDefaultsByLang = Partial<Record<Lang, SeoDefaults>>;

/** Normalises the stored seo_defaults value, which may be the new per-language
 * shape `{ en, de, el }` or a legacy flat `{ title, description, ogImage }`. */
function normalizeSeo(value: unknown): SeoDefaultsByLang | null {
  if (!value || typeof value !== "object") return null;
  const obj = value as Record<string, unknown>;
  // Legacy flat shape → treat as English defaults.
  if ("title" in obj || "description" in obj || "ogImage" in obj) {
    return { en: obj as unknown as SeoDefaults };
  }
  return obj as SeoDefaultsByLang;
}

export function useSeoDefaults(lang: Lang = "en") {
  return useQuery({
    queryKey: ["seo-defaults"],
    queryFn: async (): Promise<SeoDefaultsByLang | null> => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "seo_defaults")
        .maybeSingle();
      if (error) throw error;
      return normalizeSeo(data?.value);
    },
    select: (all): SeoDefaults | null => all?.[lang] ?? all?.en ?? null,
  });
}


export const DEFAULT_BLUEPRINT_COOLDOWN_MINUTES = 10;

// Reads the admin-configurable cooldown window (minutes between blueprint
// generations). Falls back to the default so the UI never shows a blank value.
export function useBlueprintCooldownMinutes() {
  return useQuery({
    queryKey: ["blueprint-cooldown-minutes"],
    staleTime: 5 * 60 * 1000,
    placeholderData: DEFAULT_BLUEPRINT_COOLDOWN_MINUTES,
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "blueprint_cooldown_minutes")
        .maybeSingle();
      if (error) throw error;
      const v = Number(data?.value);
      return Number.isFinite(v) && v >= 1
        ? Math.round(v)
        : DEFAULT_BLUEPRINT_COOLDOWN_MINUTES;
    },
  });
}


export const DEFAULT_BLUEPRINT_MAX_CHARS = 1000;

// Reads the admin-configurable maximum character length for the blueprint idea
// input. Falls back to the default so the UI never shows a blank value.
export function useBlueprintMaxChars() {
  return useQuery({
    queryKey: ["blueprint-max-chars"],
    staleTime: 5 * 60 * 1000,
    placeholderData: DEFAULT_BLUEPRINT_MAX_CHARS,
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "blueprint_max_chars")
        .maybeSingle();
      if (error) throw error;
      const v = Number(data?.value);
      return Number.isFinite(v) && v >= 50
        ? Math.round(v)
        : DEFAULT_BLUEPRINT_MAX_CHARS;
    },
  });
}
